import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import AnalyticsListener from './components/AnalyticsListener';
import { Toaster } from 'sonner';
import authStore from './store/authStore';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import TabBar from './components/Layout/TabBar';
import DesktopSidebar from './components/Layout/DesktopSidebar';

// Critical (above-the-fold) — sync
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import LandingPage from './pages/LandingPage';

// Lazy-loaded (heavy / less frequent)
const Search = lazy(() => import('./pages/Search'));
const Create = lazy(() => import('./pages/Create'));
const CreatePost = lazy(() => import('./pages/CreatePost'));
const Promotions = lazy(() => import('./pages/Promotions'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Professionals = lazy(() => import('./pages/Professionals'));
const ProfessionalDetail = lazy(() => import('./pages/ProfessionalDetail'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const Properties = lazy(() => import('./pages/Properties'));
const Feed = lazy(() => import('./pages/Feed'));
const Requests = lazy(() => import('./pages/Requests'));
const AIChat = lazy(() => import('./pages/AIChat'));
const Messages = lazy(() => import('./pages/Messages'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Profile = lazy(() => import('./pages/Profile'));
const Projects = lazy(() => import('./pages/Projects'));
const MyApplications = lazy(() => import('./pages/MyApplications'));
const Inspiration = lazy(() => import('./pages/Inspiration'));
const AIRedesign = lazy(() => import('./pages/AIRedesign'));
const Services = lazy(() => import('./pages/Services'));
const Favorites = lazy(() => import('./pages/Favorites'));
const CreatePromotion = lazy(() => import('./pages/CreatePromotion'));
const CreateRequest = lazy(() => import('./pages/CreateRequest'));
const CreateMarketplace = lazy(() => import('./pages/CreateMarketplace'));
const CreateProperty = lazy(() => import('./pages/CreateProperty'));
const CRM = lazy(() => import('./pages/CRM'));
const CRMQuotes = lazy(() => import('./pages/CRMQuotes'));
const CRMCatalog = lazy(() => import('./pages/CRMCatalog'));
const CRMSuggested = lazy(() => import('./pages/CRMSuggested'));
const Subscription = lazy(() => import('./pages/Subscription'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const AdminLogin = lazy(() => import('./pages/Admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const CreateCatalogItem = lazy(() => import('./pages/CreateCatalogItem'));
const CreatePortfolio = lazy(() => import('./pages/CreatePortfolio'));
const ProfessionalRegister = lazy(() => import('./pages/ProfessionalRegister'));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Rivista = lazy(() => import('./pages/Rivista'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail'));
const LegalRedirect = lazy(() => import('./pages/LegalRedirect'));
const DemoLanding = lazy(() => import('./pages/DemoLanding'));
const DemoProfile = lazy(() => import('./pages/DemoProfile'));
const DemoFeed = lazy(() => import('./pages/DemoFeed'));
const DemoExplore = lazy(() => import('./pages/DemoExplore'));

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const { googleLogin } = authStore();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace('#', '')).get('session_id');
    if (sessionId) {
      googleLogin(sessionId)
        .then((userData) => {
          window.location.hash = '';
          navigate('/', { replace: true, state: { user: userData } });
        })
        .catch(() => {
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, [googleLogin, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#10B981] border-t-transparent mx-auto mb-3" />
        <p className="text-sm text-gray-500">Accesso con Google in corso...</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = authStore();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#10B981] border-t-transparent" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const HomeOrLanding = () => {
  const { user, loading } = authStore();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#10B981] border-t-transparent" />
    </div>
  );
  if (!user) return <LandingPage />;
  return <Home />;
};

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id in hash synchronously before Routes render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
    <AnalyticsListener />
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#10B981] border-t-transparent" />
      </div>
    }>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/oauth/callback" element={<AuthCallback />} />

      <Route path="/" element={<HomeOrLanding />} />
      <Route path="/search" element={<Search />} />
      <Route path="/professionals" element={<Professionals />} />
      <Route path="/professionals/:id" element={<ProfessionalDetail />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/properties" element={<Properties />} />
      <Route path="/properties/:id" element={<PropertyDetail />} />
      <Route path="/promotions" element={<Promotions />} />
      <Route path="/feed" element={<Feed />} />
      <Route path="/requests" element={<Requests />} />
      <Route path="/inspiration" element={<Inspiration />} />
      <Route path="/services" element={<Services />} />
      <Route path="/rivista" element={<Rivista />} />
      <Route path="/rivista/:slug" element={<ArticleDetail />} />

      {/* Legal pages — redirect to backend-rendered HTML */}
      <Route path="/terms" element={<LegalRedirect target="/api/terms" />} />
      <Route path="/privacy" element={<LegalRedirect target="/api/privacy" />} />
      <Route path="/eula" element={<LegalRedirect target="/api/terms" />} />

      {/* Demo design preview (temporanee) */}
      <Route path="/crm/my-applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
      <Route path="/my-applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
      <Route path="/demo/landing" element={<DemoLanding />} />
      <Route path="/demo/profile" element={<DemoProfile />} />
      <Route path="/demo/feed" element={<DemoFeed />} />
      <Route path="/demo/explore" element={<DemoExplore />} />

      <Route path="/create" element={<ProtectedRoute><Create /></ProtectedRoute>} />
      <Route path="/create/post" element={<ProtectedRoute><CreatePost /></ProtectedRoute>} />
      <Route path="/post/create" element={<ProtectedRoute><CreatePost /></ProtectedRoute>} />
      <Route path="/ai/redesign" element={<ProtectedRoute><AIRedesign /></ProtectedRoute>} />
      <Route path="/promotions/create" element={<ProtectedRoute><CreatePromotion /></ProtectedRoute>} />
      <Route path="/requests/create" element={<ProtectedRoute><CreateRequest /></ProtectedRoute>} />
      <Route path="/marketplace/create" element={<ProtectedRoute><CreateMarketplace /></ProtectedRoute>} />
      <Route path="/properties/create" element={<ProtectedRoute><CreateProperty /></ProtectedRoute>} />
      <Route path="/catalog/create" element={<ProtectedRoute><CreateCatalogItem /></ProtectedRoute>} />
      <Route path="/portfolio/create" element={<ProtectedRoute><CreatePortfolio /></ProtectedRoute>} />
      <Route path="/portfolio/edit/:portfolioId" element={<ProtectedRoute><CreatePortfolio /></ProtectedRoute>} />
      <Route path="/professional/register" element={<ProtectedRoute><ProfessionalRegister /></ProtectedRoute>} />
      <Route path="/ai-chat" element={<ProtectedRoute><AIChat /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
      <Route path="/crm" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
      <Route path="/crm/quotes" element={<ProtectedRoute><CRMQuotes /></ProtectedRoute>} />
      <Route path="/crm/catalog" element={<ProtectedRoute><CRMCatalog /></ProtectedRoute>} />
      <Route path="/crm/suggested" element={<ProtectedRoute><CRMSuggested /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/wallet/success" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </>
  );
}

function Shell({ children }) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo/');
  if (isDemo) {
    return <main className="min-h-screen">{children}</main>;
  }
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex flex-1">
        <DesktopSidebar />
        <main className="flex-1 pb-16 md:pb-0 min-w-0">
          {children}
        </main>
      </div>
      <TabBar />
      <Footer />
    </div>
  );
}

function App() {
  const { fetchCurrentUser } = authStore();
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    if (window.location.hash?.includes('session_id=')) {
      return;
    }
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors closeButton duration={3500} />
      <Shell>
        <AppRouter />
      </Shell>
    </BrowserRouter>
  );
}

export default App;
